import {
  Box,
  Button,
  FileButton,
  Slider,
  Stack,
  useMantineTheme,
} from "@mantine/core";
import type React from "react";
import { useState } from "react";
import Cropper from "react-easy-crop";
import type { Area } from "react-easy-crop/types";
import { useLocation } from "wouter";
import { client } from "@/api-client";
import { Header } from "./Header";

interface PictureUploadProps {
  title: string;
  endpoint:
    | "/trainer/profile/portrait"
    | "/owner/profile/portrait"
    | "/owner/dog/portrait";
  aspectRatio?: number;
  maxZoom?: number;
  returnToRoute?: string;
}

export const PictureUploader: React.FC<PictureUploadProps> = ({
  title,
  endpoint,
  aspectRatio = 1,
  maxZoom = 3,
  returnToRoute,
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<Area | null>(null);
  const [_, setLocation] = useLocation();

  const fileUploadMutation = client.useMutation(
    "post",
    "/universal/file-upload",
  );
  const updatePictureMutation = client.useMutation("post", endpoint);

  const theme = useMantineTheme();

  const handleFileChange = (newFile: File | null) => {
    if (!newFile) {
      throw new Error("File is not selected");
    }
    setFile(newFile);
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(newFile);
  };

  const handleSave = async () => {
    if (!croppedArea || !file) {
      return;
    }

    const fileUpload = await fileUploadMutation.mutateAsync({});
    const formData = new FormData();
    Object.entries(fileUpload.params.fields).forEach(([k, v]) =>
      formData.append(k, v),
    );

    formData.append("file", file);

    await fetch(fileUpload.params.url, {
      method: "POST",
      body: formData,
    });
    await updatePictureMutation.mutateAsync({
      body: {
        fileUploadId: fileUpload.id,
        left: croppedArea.x,
        top: croppedArea.y,
        width: croppedArea.width,
        height: croppedArea.height,
      },
    });
    if (returnToRoute) {
      setLocation(returnToRoute);
    } else {
      window.history.back();
    }
  };

  return (
    <Stack>
      <Header title={title} />
      <Box
        size="md"
        style={{ textAlign: "center", marginTop: theme.spacing.xl }}
      >
        {imageSrc && (
          <Box
            style={{
              position: "relative",
              width: "100%",
              height: 400,
              marginBottom: theme.spacing.lg,
              border: `1px solid ${theme.colors.gray[3]}`,
            }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspectRatio}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropAreaChange={(_croppedArea, croppedAreaPixels) =>
                setCroppedArea(croppedAreaPixels)
              }
            />
          </Box>
        )}

        <Stack mt="md" gap="md" align="center">
          <Stack
            mt="md"
            gap="xs"
            align="center"
            style={{ width: 300, margin: "auto" }}
          >
            <label>Zoom</label>
            <Slider
              value={zoom}
              min={1}
              max={maxZoom}
              step={0.1}
              onChange={setZoom}
              style={{ width: "100%" }}
            />
          </Stack>
          <FileButton onChange={handleFileChange} accept="image/*">
            {(props) => (
              <Button fullWidth {...props} variant="outline">
                Pick an image
              </Button>
            )}
          </FileButton>
          <FileButton onChange={handleFileChange} accept="image/*" capture>
            {(props) => (
              <Button fullWidth {...props} variant="outline">
                Take a photo
              </Button>
            )}
          </FileButton>
          <Button
            fullWidth
            onClick={handleSave}
            disabled={!croppedArea}
            loading={
              fileUploadMutation.isPending || updatePictureMutation.isPending
            }
          >
            Save
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
